import { useTranslation } from 'react-i18next'

import {
  Badge,
  Box,
  HStack,
  TertiaryButton,
  chakra,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronLeftRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import {
  COMPACT_DATE_FIELD_TIGHT_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { WEEKDAY_KEYS } from '~/components/common/DayGrid'
import { RequiredMark } from '~/components/common/RequiredMark'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
  type FilterGridItem,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { iconSize } from '~/constants/iconSize'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { civilDateToInputValue, parseDateInputValue } from '~/models/civilDate'
import {
  addDays,
  formatDateDisplay,
  formatDateValue,
  parseIsoDate,
} from '~/models/dateTime'
import { SpotPhaseBadge } from '~/pages/SpotBidding/components/SpotPhaseBadge'
import type { SpotMarketPhase } from '~/pages/SpotBidding/models/marketPhase'

/** 受渡日 CompactCivilDateField の id (= label htmlFor / test の到達点)。 */
const SPOT_DELIVERY_DATE_ID = 'spot-page-header-delivery-date'

type Props = {
  targetDate: Date
  onTargetDateChange: (next: Date) => void
  /** 当日 (= 今日 0:00) プリセット適用 */
  onSelectToday: () => void
  /** 翌日 (= 明日 0:00) プリセット適用 */
  onSelectTomorrow: () => void
  phase: SpotMarketPhase
  /**
   * ワークフローケース由来で受渡日が固定されているか (issue #7131 /
   * AC-WFW-006a)。 true のとき datepicker・前後ナビ・当日/翌日 を disabled にする
   * (design `#spot-case-locked-hint`)。caseId 付きで開いた直後、 wfCase 取得
   * 完了前から true になる (fail-closed、 Decision 1)。
   */
  isDateLocked?: boolean
  /**
   * ケース対象日が確定済みか (= `lockedTargetDate !== undefined`)。 true の間
   * だけ Badge「ケース対象日に固定」を表示する (Design Note §States: 取得済の
   * みが対象。 取得中 / 取得失敗の間は disabled UI の隣に確定していない対象日を
   * 匂わせない)。省略時は false。
   */
  isTargetDateConfirmed?: boolean
  /**
   * 開いた条件のカードに置く発注主体区分のセレクタ (AC-BSP-131)。 ラベルはカード (FilterGrid)
   * が出すので、 部品は見えるラベルを描かない形 (`hideLabel`) で渡す。
   */
  issuerSelector?: React.ReactNode
  /** 開いた条件のカードに置く共通エリアフィルタ (AC-BSP-131)。 カードでは発注主体区分の右 (後)。 */
  areaFilter?: React.ReactNode
  /** 閉じていても出す読み取り専用の要約 (エリア・発注主体区分)。 */
  filterSummary?: readonly ToolbarStatusItem[]
  /** 既定から変えている条件の数 (エリアを 1 つ以上選んでいる時だけ 1)。 発注主体区分・受渡日は数えない。 */
  filterModifiedCount?: number
  /** 「クリア」 = エリアの選択を無しに戻す。 発注主体区分・受渡日は戻さない。 */
  onClearFilter?: () => void
}

/**
 * ページタブより上の共通ツールバー (AC-BSP-131): 開閉式フィルター
 * ([[CollapsibleFilterToolbar]]、 UI 辞書 toolbar-collapsible.md)。 操作する部品は全部カードの中
 * (受渡日 → 発注主体区分 → エリア。 発注主体区分・エリアは絞り込むタブだけ)。 閉じた行には
 * 読み取り専用の要約 (受渡日 → 発注主体 → エリア) と、 締切の状態バッジ (`status`) だけを出す。
 *
 * design `spot.html` `#spot-area-bar` / 2026-10-13 Des 判断 (#10114) に合わせ、
 * 受渡日をパンくずから分けて画面スコープを 1 か所へまとめる。
 * datepicker が受渡日の唯一の真の値で、 当日/翌日 はクイック選択 (= 当該日付を set する)。
 * 前後ナビ (◀▶) は datepicker を開かずに ±1 日ずつ移動する (spec AC-BSP-043)。
 */
export const SpotPageHeader = ({
  targetDate,
  onTargetDateChange,
  onSelectToday,
  onSelectTomorrow,
  phase,
  isDateLocked = false,
  isTargetDateConfirmed = false,
  issuerSelector,
  areaFilter,
  filterSummary = [],
  filterModifiedCount = 0,
  onClearFilter,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { dateLockedReason } = useWorkflowLockReasons()

  const handleStepDay = (step: number): void => {
    onTargetDateChange(addDays(targetDate, step))
  }

  // ケース対象日固定中 (isDateLocked) の受渡日コントロールにだけ理由 Tooltip を
  // 付ける (`DateStepper` の `disabled`/`disabledReason` と同じ契約、
  // issue #7131 Decision 6)。非固定時は素通しし DOM を変えない。
  const maybeLockTooltip = (child: React.ReactElement): React.ReactElement =>
    !isDateLocked ? (
      child
    ) : (
      <ButtonTooltip content={dateLockedReason}>{child}</ButtonTooltip>
    )

  // `<` `>` は icon だけのボタンなので、 ふだんもボタン名 (前日 / 翌日) を Tooltip で出す
  // (UI 辞書 `components/datepicker.md`「aria-label と Tooltip の両方を付ける」。 共通
  // [[DateStepper]] と同じ)。 ケース対象日固定中は固定の理由に差し替える。
  const stepTooltip = (
    label: string,
    child: React.ReactElement,
  ): React.ReactElement => (
    <ButtonTooltip content={isDateLocked ? dateLockedReason : label}>
      {child}
    </ButtonTooltip>
  )

  // 受渡日はカードの 1 項目 (見えるラベルはカードの格子が出す)。 `◀ 日付 ▶` は折り返さない 1 組
  // (▶ は必ず日付の右。 intraday の [[IntradayDeliveryDateControl]] と同じ)。 当日 / 翌日 とケース固定
  // の Badge は、 マスに入らなければ次の行へ折り返す。
  const deliveryDateControl = (
    <HStack
      alignItems="center"
      flexWrap="wrap"
      flexShrink={0}
      gap={2}
      maxWidth="100%"
    >
      {/* CompactCivilDateField 移行 (2026-08-12 デザイナー指摘: Date picker 未使用箇所の
            是正)。 見た目は据置き、 `<Text>` を `chakra.label htmlFor` に変えて
            accessible name を維持する (CompactCivilDateField は aria-label prop を
            持たず、 内部の visually-hidden native input を id + label 経由で
            名前解決する。 SubFormLabel / SingularDayForm の GridFormLabel と同じ pattern)。 */}
      <chakra.label
        css={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
        htmlFor={SPOT_DELIVERY_DATE_ID}
      >
        {t('page:biddingSpot.deliveryDate.label')}
      </chakra.label>
      {/* 前後ナビ (= ±1 日移動)。 design HTML `spot.html` `.spot-day-step` と同じ
            「◀ ピッカー ▶」 の作法。
            [rule 逸脱] icon-only は `IconButton` とする規約だが、
            ここは design の `btn-tertiary btn-square` (= border ありの副次操作) で、 隣の
            当日/翌日 preset と同一の見た目に揃える必要があるため `TertiaryButton` を使う
            (日送り nav の先例 `components/balance/FilterCard/DateNav` とも揃う)。
            icon 寸法は 8080 の `.spot-day-step` (svg 20×20) と共通 [[DateStepper]] に
            合わせて 20px を明示する。 icon-only の icon 寸法は DS 既定
            (24px) に任せる規約だが、 40px 角に固定した枠では 24px だと余白が 8px しか
            残らず 8080 と絵が変わるため、 枠を design 値で固定したのと同じ理由で例外にする。 */}
      <HStack flexShrink={0} gap={2}>
        {stepTooltip(
          t('page:biddingSpot.deliveryDate.prevDay'),
          <TertiaryButton
            // 高さは隣の日付部品 ([[CompactCivilDateField]] = 40px) と揃える。
            // size="sm" は 32px で 8px 低く、 日付欄と段差になっていた
            // (2026-08-25 デザイナー指摘 / ブラウザ実測)。
            // 8080 `.btn.btn-square { aspect-ratio: 1/1 }` に合わせ幅も 40px の正方形にする
            // (md 既定は 58px で横長になる)。 共通 [[DateStepper]] と同じ作法。
            width="40px"
            minWidth="40px"
            aria-label={t('page:biddingSpot.deliveryDate.prevDay')}
            disabled={isDateLocked}
            onClick={() => handleStepDay(-1)}
            paddingX={0}
            size="md"
          >
            <FaChevronLeftRegular
              boxSize={iconSize.labeledButton}
              aria-hidden="true"
            />
          </TertiaryButton>,
        )}
        {/* datepicker (= 受渡日の唯一の真の値)。 CompactCivilDateField (= DS Calendar
            popup ベースの共通 date field、 8080 flatpickr 相当)。 幅は共通の日付欄の幅
            (`COMPACT_DATE_FIELD_TIGHT_WIDTH`、 曜日付きの値が収まる幅)。
            isDateLocked は disabled にそのまま渡す (issue #7131: ケース対象日固定、
            CompactCivilDateField が native disabled 化 + popup 抑止を内包)。 */}
        {maybeLockTooltip(
          <Box flexShrink={0} width={COMPACT_DATE_FIELD_TIGHT_WIDTH}>
            <CompactCivilDateField
              disabled={isDateLocked}
              id={SPOT_DELIVERY_DATE_ID}
              onChange={(d) => {
                // 受渡日は「常に値がある」画面軸 (ページ全体がこの日付基準で
                // 描画される) で、 空欄は意味を持たない。 hasClear は付けず、
                // 空欄化 (d===undefined) は無視して直前値を保持する
                // (PR #5323 レビュー、2026-08-13: フィルタ/必須入力欄と異なり
                // クリア不要と判断)。
                if (d === undefined) return
                // date-only 文字列を local time として解釈する (= BalanceGeneration 等の
                // 先例に倣う)。 `parseIsoDate` は `new Date(y, m-1, d)` で local time
                // 0:00 を作るため、 従来の `new Date(\`${val}T00:00:00\`)` と同じ効果。
                const next = parseIsoDate(civilDateToInputValue(d))
                if (next !== undefined) onTargetDateChange(next)
              }}
              value={parseDateInputValue(formatDateValue(targetDate))}
            />
          </Box>,
        )}
        {stepTooltip(
          t('page:biddingSpot.deliveryDate.nextDay'),
          <TertiaryButton
            // 高さは隣の日付部品 ([[CompactCivilDateField]] = 40px) と揃える。
            // size="sm" は 32px で 8px 低く、 日付欄と段差になっていた
            // (2026-08-25 デザイナー指摘 / ブラウザ実測)。
            // 8080 `.btn.btn-square { aspect-ratio: 1/1 }` に合わせ幅も 40px の正方形にする
            // (md 既定は 58px で横長になる)。 共通 [[DateStepper]] と同じ作法。
            width="40px"
            minWidth="40px"
            aria-label={t('page:biddingSpot.deliveryDate.nextDay')}
            disabled={isDateLocked}
            onClick={() => handleStepDay(1)}
            paddingX={0}
            size="md"
          >
            <FaChevronRightRegular
              boxSize={iconSize.labeledButton}
              aria-hidden="true"
            />
          </TertiaryButton>,
        )}
      </HStack>
      {/* 当日/翌日 クイック選択 (= datepicker を当日/翌日に set する preset)。 1 組 (片方だけ次の行に落とさない) */}
      <HStack flexShrink={0} gap={2}>
        {maybeLockTooltip(
          <TertiaryButton
            disabled={isDateLocked}
            onClick={onSelectToday}
            size="md"
          >
            {t('page:biddingSpot.deliveryDate.today')}
          </TertiaryButton>,
        )}
        {maybeLockTooltip(
          <TertiaryButton
            disabled={isDateLocked}
            onClick={onSelectTomorrow}
            size="md"
          >
            {t('page:biddingSpot.deliveryDate.tomorrow')}
          </TertiaryButton>,
        )}
      </HStack>
      {/* ケース対象日固定 hint (design `#spot-case-locked-hint`、issue #7131)。
            取得済 (isTargetDateConfirmed) のときだけ出す (取得中・取得失敗の間は、確定していない対象日を出さない)。 */}
      {isTargetDateConfirmed && (
        <Badge status="info">{t('common:filter.caseLockedBadge')}</Badge>
      )}
    </HStack>
  )

  // カードの並び: 受渡日 → 発注主体区分 (出す時だけ) → エリア (出す時だけ)。 エリアの候補は発注主体区分で
  // 決まるので、 エリアは発注主体区分の右に置く (2026-10-29・10-30 ユーザー指示「画面上部のエリアが表示される
  // 際は、 フィルターの一番左ではなく、 発注主体の右に」。 UI 辞書 toolbar-collapsible.md も同じ決まり)。
  const filterItems: FilterGridItem[] = [
    {
      key: 'deliveryDate',
      label: t('page:biddingSpot.deliveryDate.label'),
      control: deliveryDateControl,
    },
    ...(issuerSelector !== undefined
      ? [
          {
            key: 'issuer',
            label: (
              <>
                {t('page:biddingCommon.issuerSelector.label')}
                <RequiredMark />
              </>
            ),
            control: issuerSelector,
          },
        ]
      : []),
    ...(areaFilter !== undefined
      ? [
          {
            key: 'area',
            label: t('page:biddingSpot.form.area'),
            control: areaFilter,
          },
        ]
      : []),
  ]

  // 要約の日付は日付欄の閉じた表示と同じ `YYYY/MM/DD (曜)`。 並びはカードと同じ 受渡日 → 発注主体 → エリア。
  const deliveryDateSummary: ToolbarStatusItem = {
    key: 'deliveryDate',
    label: t('page:biddingSpot.deliveryDate.label'),
    value: t('common:date.withWeekday', {
      date: formatDateDisplay(targetDate),
      weekday: t(
        `common:date.calendar.weekday.${WEEKDAY_KEYS[targetDate.getDay()]}`,
      ),
    }),
  }
  const summary: ToolbarStatusItem[] = [
    deliveryDateSummary,
    ...filterSummary.filter((item) => item.key !== 'area'),
    ...filterSummary.filter((item) => item.key === 'area'),
  ]

  return (
    <Box
      flexShrink={0}
      aria-label={t('page:biddingSpot.breadcrumb.page')}
      role="toolbar"
    >
      <CollapsibleFilterToolbar
        // 並びは画面で決める (受渡日 → 発注主体区分 → エリア)。 既定の [[pinAreaAndPeriodFirst]] は
        // エリアを先頭へ寄せるので、 カードと要約の両方で渡した順のまま並べる
        keepSummaryOrder
        modifiedCount={filterModifiedCount}
        onClear={onClearFilter}
        status={
          // 締切の状態は読み取り専用なので閉じていても出す (受付中は残り時間のカウントダウン、 AC-BSP-044)。
          <SpotPhaseBadge phase={phase} targetDate={targetDate} />
        }
        storageKey="biddingSpot.commonToolbar"
        summary={summary}
      >
        <FilterGrid items={filterItems} keepOrder />
      </CollapsibleFilterToolbar>
    </Box>
  )
}
